import { useState } from "react";
import { api } from "../lib/api.js";
import type { Project } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";

/**
 * A deliberately high-friction, irreversible delete flow shared by Project
 * Settings and the library. Three gates: (1) open the dialog, (2) type the exact
 * book title AND tick the acknowledgement, (3) a final "last chance" confirm
 * before the request fires. Works for manuscripts and coloring books.
 */
export function DeleteBookDialog({
  project,
  onCancel,
  onDeleted
}: {
  project: Pick<Project, "id" | "title" | "type">;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const toast = useToast();
  const [confirmText, setConfirmText] = useState("");
  const [ack, setAck] = useState(false);
  const [finalStep, setFinalStep] = useState(false);
  const [busy, setBusy] = useState(false);

  const isColoring = project.type === "coloring";
  const kind = isColoring ? "coloring book" : "book";
  const titleMatches = confirmText.trim() === project.title.trim();
  const canProceed = titleMatches && ack && !busy;

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.deleteProject(project.id);
      toast.success(`“${project.title}” was permanently deleted.`);
      onDeleted();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not delete the book.");
      setBusy(false);
    }
  };

  return (
    <div className="pset-danger-backdrop" onMouseDown={busy ? undefined : onCancel}>
      <div className="pset-danger-dialog card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="pset-danger-dialog-head">
          <span className="pset-danger-badge"><Icon name="alert" size={16} /></span>
          <div>
            <div className="strong" style={{ fontSize: 15 }}>Delete “{project.title}”?</div>
            <div className="faint" style={{ fontSize: 12 }}>This permanently deletes the entire {kind}. There is no undo.</div>
          </div>
        </div>

        {!finalStep ? (
          <>
            <ul className="pset-danger-list">
              {isColoring ? (
                <>
                  <li>Every page and all generated images and variants</li>
                  <li>The cover, prompts, and page ordering</li>
                  <li>All export history for this coloring book</li>
                </>
              ) : (
                <>
                  <li>All chapters and scenes, and their full revision history</li>
                  <li>Characters, relationships, locations, timeline, plot threads, notes</li>
                  <li>Audiobook renders, review annotations, and cached intelligence</li>
                </>
              )}
            </ul>
            <label className="field" style={{ margin: 0 }}>
              <span className="label">Type the title to confirm: <span className="strong">{project.title}</span></span>
              <input className="input" value={confirmText} autoFocus
                onChange={(e) => setConfirmText(e.target.value)} placeholder={project.title} />
            </label>
            <label className="pset-danger-ack">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
              <span>I understand this is permanent and cannot be undone.</span>
            </label>
            <div className="row gap-2" style={{ justifyContent: "flex-end" }}>
              <button className="btn btn-sm btn-ghost" onClick={onCancel}>Cancel</button>
              <button className="btn btn-sm btn-danger" disabled={!canProceed} onClick={() => setFinalStep(true)}>
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="pset-danger-final">
              <Icon name="alert" size={18} />
              <div>Last chance. Clicking below will permanently delete <span className="strong">“{project.title}”</span> and everything in it, immediately and irreversibly.</div>
            </div>
            <div className="row gap-2" style={{ justifyContent: "flex-end" }}>
              <button className="btn btn-sm btn-ghost" onClick={() => setFinalStep(false)} disabled={busy}>Go back</button>
              <button className="btn btn-sm btn-danger" onClick={() => void doDelete()} disabled={busy}>
                {busy ? <><Icon name="clock" size={13} className="spin" /> Deleting…</> : <><Icon name="trash" size={14} /> Delete forever</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
